'use client';
import { useState, useEffect } from 'react';
import Header from '@/components/common/Header';
import Footer from '@/components/common/Footer';
import ProductFilterTabs from './components/ProductFilterTabs';
import ProductCard from './components/ProductCard';
import Icon from '@/components/ui/AppIcon';
import Link from 'next/link';

export default function ProductsPage() {
  const [activeCategory, setActiveCategory] = useState('All Products');

  const categories = [
  'All Products',
  'Cooking Oils',
  'Wood Pellets',
  'Chicken Feed',
  'Fertilizers',
  'Iron Scraps',
  'Whey Proteins',
  'Animal Feeds',
  'Copper Scraps',
  'Copy Papers',
  'Truck Tires'];


  const products = [
  // Cooking Oils
  {
    id: 'prod_sunflower_oil',
    name: 'Sunflower Oil',
    category: 'Cooking Oils',
    description: 'Premium refined sunflower oil with high oleic content. Perfect for cooking and food manufacturing. Consistent quality in every batch.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b5eaf999-1765022942690.png",
    alt: 'Sunflower oil bottle with golden liquid showing premium quality',
    specifications: {
      purity: '99.9% Pure',
      packaging: '1L, 5L, 20L bottles, Bulk tankers',
      minimumOrder: '500 kg'
    }
  },
  {
    id: 'prod_canola_oil',
    name: 'Canola Oil',
    category: 'Cooking Oils',
    description: 'Low saturated fat canola oil. Ideal for health-conscious food production and frying. Great for restaurants and food manufacturers.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1f6ddb513-1770099088729.png",
    alt: 'Canola oil container showing healthy cooking oil option',
    specifications: {
      purity: 'Food Grade',
      packaging: 'Bulk containers, 20L drums',
      minimumOrder: '1000 kg'
    }
  },
  {
    id: 'prod_palm_oil',
    name: 'Palm Oil',
    category: 'Cooking Oils',
    description: 'Refined palm oil with excellent stability. Perfect for industrial food processing and bakery applications. Long shelf life.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_116c870f9-1768054047717.png",
    alt: 'Palm oil showing rich golden color for food processing',
    specifications: {
      purity: 'Refined Food Grade',
      packaging: 'Flexitanks, Drums',
      minimumOrder: '2000 kg'
    }
  },
  {
    id: 'prod_soybean_oil',
    name: 'Soybean Oil',
    category: 'Cooking Oils',
    description: 'Versatile soybean oil for cooking, salad dressings, and industrial use. Reliable quality for all your needs.',
    image: "https://images.unsplash.com/photo-1629375967748-9bc9bf6c53df",
    alt: 'Soybean oil bottle showing versatile cooking oil',
    specifications: {
      purity: 'Industrial Grade',
      packaging: 'Bulk tankers, IBC containers',
      minimumOrder: '1500 kg'
    }
  },
  {
    id: 'prod_olive_oil',
    name: 'Olive Oil',
    category: 'Cooking Oils',
    description: 'Extra virgin and refined olive oil. Perfect for premium culinary and retail applications. Authentic Mediterranean quality.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_196b445bb-1764778043502.png",
    alt: 'Premium olive oil bottle showing high-quality Mediterranean oil',
    specifications: {
      purity: 'Extra Virgin / Refined',
      packaging: '500ml, 1L, 5L bottles',
      minimumOrder: '300 kg'
    }
  },

  // Chicken Feed
  {
    id: 'prod_layer_feed',
    name: 'Layer Feed',
    category: 'Chicken Feed',
    description: 'High-protein formula for optimal egg production. Trusted by poultry farms across Europe. Consistent results every time.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_172b55ec1-1769325082021.png",
    alt: 'Layer chicken feed showing high-protein poultry nutrition',
    specifications: {
      packaging: '25kg, 50kg bags, Bulk',
      minimumOrder: '1000 kg'
    }
  },
  {
    id: 'prod_broiler_feed',
    name: 'Broiler Feed',
    category: 'Chicken Feed',
    description: 'Growth-optimized feed for rapid weight gain. Perfect for broiler chickens. Proven formula for best results.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_114025c7c-1770939793882.png",
    alt: 'Broiler chicken feed showing growth formula nutrition',
    specifications: {
      packaging: '25kg, 50kg bags',
      minimumOrder: '1500 kg'
    }
  },
  {
    id: 'prod_starter_feed',
    name: 'Starter Feed',
    category: 'Chicken Feed',
    description: 'Specially formulated for chicks in their first weeks. Gives them the best start for healthy growth.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1afbe3e09-1770939795069.png",
    alt: 'Starter chicken feed for young chicks showing early nutrition',
    specifications: {
      packaging: '20kg, 40kg bags',
      minimumOrder: '800 kg'
    }
  },
  {
    id: 'prod_organic_feed',
    name: 'Organic Feed',
    category: 'Chicken Feed',
    description: 'Certified organic chicken feed for eco-friendly farming. Perfect for sustainable poultry operations.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1f209eede-1770939794051.png",
    alt: 'Organic chicken feed showing certified eco-friendly nutrition',
    specifications: {
      packaging: '25kg bags, Certified Organic',
      minimumOrder: '500 kg'
    }
  },
  {
    id: 'prod_grower_feed',
    name: 'Grower Feed',
    category: 'Chicken Feed',
    description: 'Balanced nutrition for chickens in their medium growth stage. Supports healthy development.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1fe4e481d-1769325083559.png",
    alt: 'Grower chicken feed for medium-stage poultry nutrition',
    specifications: {
      packaging: '30kg, 50kg bags',
      minimumOrder: '1200 kg'
    }
  },

  // Fertilizers
  {
    id: 'prod_npk_fertilizer',
    name: 'NPK Fertilizer 20-20-20',
    category: 'Fertilizers',
    description: 'Balanced NPK formula for all-purpose crop nutrition. Maximize your yield with this proven formula.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_17bc3ff46-1765051026926.png",
    alt: 'NPK fertilizer granules showing balanced crop nutrition',
    specifications: {
      purity: '20-20-20 Ratio',
      packaging: '25kg, 50kg bags',
      minimumOrder: '2000 kg'
    }
  },
  {
    id: 'prod_urea',
    name: 'Urea Fertilizer',
    category: 'Fertilizers',
    description: 'High-nitrogen urea for rapid plant growth. Perfect for green foliage development. Fast-acting results.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1c5c1d5ba-1770939794056.png",
    alt: 'Urea fertilizer showing high-nitrogen plant nutrition',
    specifications: {
      purity: '46% Nitrogen',
      packaging: 'Bulk, 50kg bags',
      minimumOrder: '5000 kg'
    }
  },
  {
    id: 'prod_phosphate',
    name: 'Phosphate Fertilizer',
    category: 'Fertilizers',
    description: 'Phosphorus-rich fertilizer for strong roots and flowering. Essential for healthy plant development.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1dfc3cbba-1766332567603.png",
    alt: 'Phosphate fertilizer showing root development nutrients',
    specifications: {
      purity: 'High Phosphorus',
      packaging: '40kg, 50kg bags',
      minimumOrder: '3000 kg'
    }
  },
  {
    id: 'prod_potash',
    name: 'Potash Fertilizer',
    category: 'Fertilizers',
    description: 'Potassium source for improved crop quality. Boosts disease resistance and stress tolerance.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_182b0af4e-1770939797635.png",
    alt: 'Potash fertilizer showing potassium for crop quality',
    specifications: {
      purity: 'Muriate of Potash',
      packaging: '50kg bags, Bulk',
      minimumOrder: '2500 kg'
    }
  },
  {
    id: 'prod_organic_compost',
    name: 'Organic Compost',
    category: 'Fertilizers',
    description: 'Eco-friendly organic compost for sustainable farming. Improves soil health naturally.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_140322a9d-1770939792106.png",
    alt: 'Organic compost showing eco-friendly soil nutrients',
    specifications: {
      packaging: '30kg bags, Bulk',
      minimumOrder: '1000 kg'
    }
  },

  // Wood Pellets
  {
    id: 'prod_premium_pellets',
    name: 'Premium Wood Pellets 6mm',
    category: 'Wood Pellets',
    description: 'High-quality 6mm wood pellets for residential and commercial heating. Clean burning with low ash content.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1e9ca589f-1768646377809.png",
    alt: 'Premium wood pellets showing consistent 6mm size for heating',
    specifications: {
      purity: 'Low Ash Content',
      packaging: '15kg bags, 1 ton pallets',
      minimumOrder: '1000 kg'
    }
  },
  {
    id: 'prod_industrial_pellets',
    name: 'Industrial Wood Pellets',
    category: 'Wood Pellets',
    description: 'Bulk wood pellets for large-scale industrial heating and power generation. Reliable energy source.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1c3515485-1770939795052.png",
    alt: 'Industrial wood pellets for large-scale energy production',
    specifications: {
      packaging: 'Bulk delivery, Silo trucks',
      minimumOrder: '10 tons'
    }
  },
  {
    id: 'prod_hardwood_pellets',
    name: 'Hardwood Pellets',
    category: 'Wood Pellets',
    description: 'Dense hardwood pellets with high heat output. Long burn time for maximum efficiency.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1cf2d0955-1770939793313.png",
    alt: 'Hardwood pellets showing high heat output biomass',
    specifications: {
      purity: 'Premium Hardwood',
      packaging: '15kg, 20kg bags',
      minimumOrder: '1500 kg'
    }
  },
  {
    id: 'prod_softwood_pellets',
    name: 'Softwood Pellets',
    category: 'Wood Pellets',
    description: 'Easy-ignition softwood pellets ideal for residential pellet stoves.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1241aa195-1770939793246.png",
    alt: 'Softwood pellets showing easy-ignition heating fuel',
    specifications: {
      packaging: '10kg, 15kg bags',
      minimumOrder: '800 kg'
    }
  },

  // Iron Scraps
  {
    id: 'prod_hms',
    name: 'HMS 1 & 2 Scrap',
    category: 'Iron Scraps',
    description: 'Heavy melting scrap metal for steel mills and recycling facilities.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1956b083d-1770939797813.png",
    alt: 'HMS iron scrap showing heavy melting steel for recycling',
    specifications: {
      purity: 'Industrial Grade',
      packaging: 'Bulk containers, 20ft/40ft',
      minimumOrder: '50 tons'
    }
  },
  {
    id: 'prod_shredded_steel',
    name: 'Shredded Steel Scrap',
    category: 'Iron Scraps',
    description: 'Processed shredded steel scrap ready for melting and reuse.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_152a84132-1768657923838.png",
    alt: 'Shredded steel scrap showing processed recyclable metal',
    specifications: {
      packaging: 'Bulk, Containers',
      minimumOrder: '30 tons'
    }
  },
  {
    id: 'prod_cast_iron',
    name: 'Cast Iron Scrap',
    category: 'Iron Scraps',
    description: 'High-quality cast iron scrap for foundries and manufacturing.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1a6d7a5d3-1770939792357.png",
    alt: 'Cast iron scrap showing industrial-grade recyclable metal',
    specifications: {
      purity: 'Foundry Grade',
      packaging: 'Bulk delivery',
      minimumOrder: '40 tons'
    }
  },

  // Whey Proteins
  {
    id: 'prod_whey_concentrate',
    name: 'Whey Protein Concentrate (WPC 80)',
    category: 'Whey Proteins',
    description: 'High-quality whey protein concentrate with 80% protein content. Perfect for sports nutrition, dietary supplements, and food fortification.',
    image: "https://images.unsplash.com/photo-1525093996726-e6bcd6db395a",
    alt: 'Whey protein concentrate powder showing high-quality sports nutrition supplement',
    specifications: {
      purity: '80% Protein',
      packaging: '25kg bags, Bulk',
      minimumOrder: '500 kg'
    }
  },
  {
    id: 'prod_whey_isolate',
    name: 'Whey Protein Isolate (WPI 90)',
    category: 'Whey Proteins',
    description: 'Premium whey protein isolate with 90% protein content. Low lactose, ideal for high-performance nutrition products and protein shakes.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1c562721f-1764688967586.png",
    alt: 'Whey protein isolate showing premium low-lactose protein supplement',
    specifications: {
      purity: '90% Protein',
      packaging: '20kg bags, Custom packaging',
      minimumOrder: '300 kg'
    }
  },
  {
    id: 'prod_whey_hydrolysate',
    name: 'Whey Protein Hydrolysate',
    category: 'Whey Proteins',
    description: 'Fast-absorbing hydrolyzed whey protein for rapid muscle recovery. Perfect for premium sports nutrition and medical nutrition products.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1e7087467-1771831797802.png",
    alt: 'Whey protein hydrolysate showing fast-absorbing protein supplement',
    specifications: {
      purity: 'Hydrolyzed Formula',
      packaging: '15kg, 25kg bags',
      minimumOrder: '250 kg'
    }
  },
  {
    id: 'prod_whey_blend',
    name: 'Whey Protein Blend',
    category: 'Whey Proteins',
    description: 'Cost-effective blend of concentrate and isolate. Balanced nutrition for mass-market protein supplements and functional foods.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_105dd0713-1766478892019.png",
    alt: 'Whey protein blend showing balanced nutrition supplement',
    specifications: {
      purity: 'Blended Formula',
      packaging: '25kg bags, Bulk',
      minimumOrder: '600 kg'
    }
  },

  // Animal Feeds
  {
    id: 'prod_cattle_feed',
    name: 'Cattle Feed Pellets',
    category: 'Animal Feeds',
    description: 'Complete nutrition pellets for dairy and beef cattle. Optimized for milk production and weight gain with balanced minerals and vitamins.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1bde9b6de-1770207000661.png",
    alt: 'Cattle feed pellets showing complete nutrition for dairy and beef cattle',
    specifications: {
      packaging: '50kg bags, Bulk',
      minimumOrder: '2000 kg'
    }
  },
  {
    id: 'prod_poultry_feed',
    name: 'Poultry Feed Mix',
    category: 'Animal Feeds',
    description: 'All-purpose poultry feed for chickens, ducks, and turkeys. High-energy formula for optimal growth and egg production.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1da8738cd-1768060558833.png",
    alt: 'Poultry feed mix showing nutrition for chickens and birds',
    specifications: {
      packaging: '25kg, 50kg bags',
      minimumOrder: '1500 kg'
    }
  },
  {
    id: 'prod_pig_feed',
    name: 'Pig Feed Concentrate',
    category: 'Animal Feeds',
    description: 'High-protein pig feed for all growth stages. Formulated for efficient weight gain and healthy development in swine farming.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_10cf814d0-1765444473447.png",
    alt: 'Pig feed concentrate showing high-protein swine nutrition',
    specifications: {
      packaging: '40kg, 50kg bags',
      minimumOrder: '1800 kg'
    }
  },
  {
    id: 'prod_fish_feed',
    name: 'Fish Feed Pellets',
    category: 'Animal Feeds',
    description: 'Floating and sinking pellets for aquaculture. Specially formulated for tilapia, catfish, and other commercial fish species.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_13195bfe3-1766488123570.png",
    alt: 'Fish feed pellets showing aquaculture nutrition for commercial fish farming',
    specifications: {
      packaging: '20kg, 30kg bags',
      minimumOrder: '1000 kg'
    }
  },
  {
    id: 'prod_sheep_feed',
    name: 'Sheep & Goat Feed',
    category: 'Animal Feeds',
    description: 'Specialized feed for sheep and goats. Rich in fiber and minerals for wool production and healthy growth.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_17ece5f38-1765513525216.png",
    alt: 'Sheep and goat feed showing specialized nutrition for small ruminants',
    specifications: {
      packaging: '25kg, 50kg bags',
      minimumOrder: '1200 kg'
    }
  },

  // Copper Scraps
  {
    id: 'prod_copper_wire_scrap',
    name: 'Copper Wire Scrap',
    category: 'Copper Scraps',
    description: 'Clean copper wire scrap with minimal insulation. High-grade material perfect for recycling and copper recovery operations.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_109cf6478-1768242166907.png",
    alt: 'Copper wire scrap showing clean recyclable copper material',
    specifications: {
      purity: '95-98% Copper',
      packaging: 'Bulk containers, 20ft/40ft',
      minimumOrder: '20 tons'
    }
  },
  {
    id: 'prod_millberry_copper',
    name: 'Millberry Copper Scrap',
    category: 'Copper Scraps',
    description: 'Premium Grade A millberry copper with 99.9% purity. Bare bright copper wire ideal for high-value recycling and manufacturing.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_109cf6478-1768242166907.png",
    alt: 'Millberry copper scrap showing premium 99.9% pure copper wire',
    specifications: {
      purity: '99.9% Pure Copper',
      packaging: 'Bulk, Containers',
      minimumOrder: '15 tons'
    }
  },
  {
    id: 'prod_copper_cathodes',
    name: 'Copper Cathodes',
    category: 'Copper Scraps',
    description: 'Electrolytic copper cathodes with 99.99% purity. Premium quality for electrical manufacturing and industrial applications.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1f2d6471b-1768133007820.png",
    alt: 'Copper cathodes showing high-purity electrolytic copper sheets',
    specifications: {
      purity: '99.99% Pure',
      packaging: 'Palletized, Containers',
      minimumOrder: '25 tons'
    }
  },
  {
    id: 'prod_copper_birch_cliff',
    name: 'Birch Cliff Copper Scrap',
    category: 'Copper Scraps',
    description: 'Mixed copper scrap with some oxidation. Cost-effective option for general recycling and secondary copper production.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1dc7d5df6-1771831797502.png",
    alt: 'Birch cliff copper scrap showing mixed recyclable copper material',
    specifications: {
      purity: '92-94% Copper',
      packaging: 'Bulk delivery',
      minimumOrder: '30 tons'
    }
  },

  // Copy Papers
  {
    id: 'prod_a4_copy_paper',
    name: 'A4 Copy Paper 80gsm',
    category: 'Copy Papers',
    description: 'Premium A4 copy paper 80gsm. Bright white, smooth finish for high-quality printing. Perfect for offices, schools, and print shops.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1771d904e-1765016918238.png",
    alt: 'A4 copy paper showing premium white office printing paper',
    specifications: {
      purity: '80gsm, 500 sheets/ream',
      packaging: '5 reams/box, 2500 sheets',
      minimumOrder: '100 reams'
    }
  },
  {
    id: 'prod_a3_copy_paper',
    name: 'A3 Copy Paper 80gsm',
    category: 'Copy Papers',
    description: 'Large format A3 copy paper for posters, presentations, and architectural drawings. High brightness and excellent print quality.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_11a3fa7e9-1765097737622.png",
    alt: 'A3 copy paper showing large format printing paper',
    specifications: {
      purity: '80gsm, 500 sheets/ream',
      packaging: '3 reams/box',
      minimumOrder: '50 reams'
    }
  },
  {
    id: 'prod_letter_size_paper',
    name: 'Letter Size Paper 20lb',
    category: 'Copy Papers',
    description: 'US Letter size (8.5" x 11") copy paper. Standard 20lb weight for everyday printing, copying, and document creation.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b4f451fb-1768521387744.png",
    alt: 'Letter size copy paper showing US standard office paper',
    specifications: {
      purity: '20lb (75gsm), 500 sheets/ream',
      packaging: '10 reams/case',
      minimumOrder: '80 reams'
    }
  },
  {
    id: 'prod_a4_premium_paper',
    name: 'A4 Premium Paper 100gsm',
    category: 'Copy Papers',
    description: 'Heavy-weight A4 paper 100gsm for professional documents, presentations, and high-quality prints. Extra smooth finish.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_10f84fc0d-1769066273586.png",
    alt: 'Premium A4 paper showing heavy-weight professional printing paper',
    specifications: {
      purity: '100gsm, 500 sheets/ream',
      packaging: '5 reams/box',
      minimumOrder: '60 reams'
    }
  },

  // Truck Tires
  {
    id: 'prod_tire_11r225',
    name: 'Truck Tire 11R22.5',
    category: 'Truck Tires',
    description: 'Heavy-duty 11R22.5 truck tire for long-haul applications. Excellent tread life and fuel efficiency for commercial vehicles.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b14bc609-1764649767887.png",
    alt: 'Truck tire 11R22.5 showing heavy-duty commercial vehicle tire',
    specifications: {
      purity: '16 PR Rating',
      packaging: 'Individual, Bulk orders',
      minimumOrder: '20 units'
    }
  },
  {
    id: 'prod_tire_29575r225',
    name: 'Truck Tire 295/75R22.5',
    category: 'Truck Tires',
    description: 'Wide base 295/75R22.5 tire for improved stability and load capacity. Ideal for regional and long-distance trucking.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b14bc609-1764649767887.png",
    alt: 'Truck tire 295/75R22.5 showing wide base commercial tire',
    specifications: {
      purity: '14 PR Rating',
      packaging: 'Individual, Palletized',
      minimumOrder: '16 units'
    }
  },
  {
    id: 'prod_tire_28575r245',
    name: 'Truck Tire 285/75R24.5',
    category: 'Truck Tires',
    description: 'All-position 285/75R24.5 tire for versatile truck applications. Durable construction for mixed highway and urban use.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b14bc609-1764649767887.png",
    alt: 'Truck tire 285/75R24.5 showing all-position commercial tire',
    specifications: {
      purity: '16 PR Rating',
      packaging: 'Individual units',
      minimumOrder: '18 units'
    }
  },
  {
    id: 'prod_tire_31580r225',
    name: 'Truck Tire 315/80R22.5',
    category: 'Truck Tires',
    description: 'Drive axle 315/80R22.5 tire with aggressive tread pattern. Maximum traction for heavy loads and challenging conditions.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b745a5b7-1770266442714.png",
    alt: 'Truck tire 315/80R22.5 showing drive axle tire with aggressive tread',
    specifications: {
      purity: '18 PR Rating',
      packaging: 'Individual, Bulk',
      minimumOrder: '24 units'
    }
  },
  {
    id: 'prod_tire_38565r225',
    name: 'Truck Tire 385/65R22.5',
    category: 'Truck Tires',
    description: 'Super single 385/65R22.5 tire for weight reduction and fuel savings. Wide footprint for excellent stability.',
    image: "https://images.unsplash.com/photo-1560288492-8c358b6ab436",
    alt: 'Truck tire 385/65R22.5 showing super single wide tire',
    specifications: {
      purity: '20 PR Rating',
      packaging: 'Individual units',
      minimumOrder: '12 units'
    }
  }];


  const filteredProducts = activeCategory === 'All Products' ?
  products :
  products?.filter((p) => p?.category === activeCategory);

  useEffect(() => {
    const reveals = document.querySelectorAll('.reveal');
    if (!reveals?.length) return;

    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add('active')),
      { threshold: 0.1 }
    );
    reveals?.forEach((el) => observer?.observe(el));

    return () => observer?.disconnect();
  }, []);

  return (
    <div className="min-h-screen">
      <Header />
      <main className="py-24 bg-background">
        <div className="max-w-container mx-auto px-6 md:px-12">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-8 reveal">
            <Link href="/" className="hover:text-primary transition-colors">Home</Link>
            <Icon name="ChevronRightIcon" size={16} />
            <span className="text-foreground font-medium">Products</span>
          </div>

          {/* Page Header */}
          <div className="text-center mb-12 reveal">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-foreground mb-6">
              Our Product Range
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Premium quality supplies for global markets. Browse our extensive catalog of cooking oils, wood pellets, chicken feed, fertilizers, and iron scraps.
            </p>
          </div>

          {/* Filter Tabs */}
          <div className="reveal">
            <ProductFilterTabs
              categories={categories}
              activeCategory={activeCategory}
              onCategoryChange={setActiveCategory} />
            
          </div>

          {/* Products Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {filteredProducts?.map((product, index) =>
            <div
              key={product?.id}
              className="reveal"
              style={{ transitionDelay: `${index * 50}ms` }}>
              
                <ProductCard product={product} />
              </div>
            )}
          </div>

          {/* Bulk Order CTA */}
          <div className="bg-gradient-to-r from-primary/10 to-secondary/10 rounded-organic-lg p-8 md:p-12 text-center reveal">
            <Icon name="TruckIcon" size={48} className="text-primary mx-auto mb-4" />
            <h3 className="text-2xl md:text-3xl font-serif font-bold text-foreground mb-4">
              Need Bulk Quantities?
            </h3>
            <p className="text-lg text-muted-foreground mb-6 max-w-2xl mx-auto">
              Contact us for special pricing on large orders. Our team will work with you to find the best solution for your business needs.
            </p>
            <Link
              href="/contact"
              className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-8 py-4 rounded-full text-base font-semibold hover:bg-primary/90 shadow-lg btn-scale">
              
              Request Bulk Quote
              <Icon name="ArrowUpRightIcon" size={20} className="arrow-shift" />
            </Link>
          </div>

          {/* Quality Certifications */}
          <div className="mt-16 pt-16 border-t border-border reveal">
            <h3 className="text-2xl font-serif font-bold text-center text-foreground mb-8">
              Quality Certifications
            </h3>
            <div className="flex flex-wrap justify-center items-center gap-12 opacity-70 grayscale hover:grayscale-0 hover:opacity-100 transition-all duration-500">
              <div className="text-center">
                <Icon name="ShieldCheckIcon" size={48} className="text-primary mx-auto mb-2" />
                <div className="text-sm font-semibold uppercase tracking-wider text-foreground">ISO Certified</div>
              </div>
              <div className="text-center">
                <Icon name="CheckBadgeIcon" size={48} className="text-primary mx-auto mb-2" />
                <div className="text-sm font-semibold uppercase tracking-wider text-foreground">Quality Standards</div>
              </div>
              <div className="text-center">
                <Icon name="GlobeAltIcon" size={48} className="text-primary mx-auto mb-2" />
                <div className="text-sm font-semibold uppercase tracking-wider text-foreground">Global Shipping</div>
              </div>
              <div className="text-center">
                <Icon name="TrophyIcon" size={48} className="text-primary mx-auto mb-2" />
                <div className="text-sm font-semibold uppercase tracking-wider text-foreground">Industry Leader</div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>);


}